'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Grid, SelectField, TextField } from '@petsmart-ui/sparky';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CA_PROVINCES, getNextApiRoute, US_STATES, zipCodeSetter } from '@libs/common-next';
import { HttpMethod, isLocaleCA, removeSpecialCharacters } from '@libs/core';
import { CountryTarget } from '../../constants';
import { useFetchWithSnackbarErrorHandling } from '../../hooks/useFetchWithSnackbarErrorHandling';
import { StreetAddressLine } from './StreetAddressLine';
import type { AddressFormValues, FormatAddressResponseInterface, SuggestedAddressInterface, SuggestionResponseInterface } from './types';
import { applyFormattedAddressToFormik, applyFormattedAddressToRHF, createSuggestedAddressSelectionHandler, getFieldErrorMessage } from './utils';
import { provinceRequiredMsg, stateRequiredMsg } from './validationErrors';
import './AddressForm.scss';
export type { FormatAddressResponseInterface, FormattedAddressFieldInterface, SuggestedAddressInterface, SuggestionResponseInterface } from './types';
type AddressFormProps = Readonly<{
  addressSuggestionRoute: string;
  aptFieldDefaultOpen?: boolean;
  countryTarget?: CountryTarget;
  formLibrary?: 'formik' | 'rhf';
}>;
export function AddressForm({
  aptFieldDefaultOpen,
  countryTarget,
  formLibrary = 'formik'
}: AddressFormProps) {
  const [suggestedAddresses, setSuggestedAddresses] = useState<SuggestedAddressInterface[] | null>(null);
  const [aptFieldOpen, setAptFieldOpen] = useState(Boolean(aptFieldDefaultOpen));
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    fetchWithSnackbarErrorHandling
  } = useFetchWithSnackbarErrorHandling();
  const statesOrProvinces = useMemo(() => {
    if (countryTarget === CountryTarget.BOTH) {
      const combined = [...US_STATES, ...CA_PROVINCES];
      return combined.sort((a, b) => a.name.localeCompare(b.name));
    }
    return isLocaleCA() ? CA_PROVINCES : US_STATES;
  }, [countryTarget]);
  useEffect(() => {
    if (aptFieldDefaultOpen) {
      setAptFieldOpen(true);
    }
  }, [aptFieldDefaultOpen]);
  const checkAddressSuggestions = useCallback(async (suggestAddressInput: string): Promise<SuggestionResponseInterface | null> => {
    try {
      const response = await fetchWithSnackbarErrorHandling<SuggestionResponseInterface>(`${getNextApiRoute('address/suggest-address-edq')}?query=${suggestAddressInput}&country=${countryTarget}`,
      // NOSONAR - deprecated function is still required for existing API routes
      {
        method: HttpMethod.GET
      });
      if (response === null) {
        return null;
      }
      setSuggestedAddresses(response.results);
      return response;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWithSnackbarErrorHandling, countryTarget]);
  const formatSuggestedAddress = useCallback(async (suggestedAddressId: string, country: string | null): Promise<FormatAddressResponseInterface | null> => {
    try {
      return await fetchWithSnackbarErrorHandling<FormatAddressResponseInterface>(`${getNextApiRoute('address/format-address-edq')}?id=${suggestedAddressId}&country=${country}`,
      // NOSONAR - deprecated function is still required for existing API routes
      {
        method: HttpMethod.GET
      });
    } catch {
      return null;
    }
  }, [fetchWithSnackbarErrorHandling]);
  const handleSuggestedAddressClick = async (suggestedAddress: SuggestedAddressInterface) => {
    const {
      searchParams
    } = new URL(suggestedAddress.format);
    const id = searchParams.get('id');
    const country_0 = searchParams.get('country');
    if (!id) {
      setDropdownIsOpen(false);
      return null;
    }
    const formatAddressResponse = await formatSuggestedAddress(id, country_0);
    setDropdownIsOpen(false);
    return formatAddressResponse;
  };
  if (formLibrary === 'rhf') {
    return <AddressFormRHF aptFieldOpen={aptFieldOpen} countryTarget={countryTarget} statesOrProvinces={statesOrProvinces} dropdownIsOpen={dropdownIsOpen} suggestedAddresses={suggestedAddresses} loading={loading} setLoading={setLoading} checkAddressSuggestions={checkAddressSuggestions} setDropdownIsOpen={setDropdownIsOpen} setSuggestedAddresses={setSuggestedAddresses} setAptFieldOpen={setAptFieldOpen} handleSuggestedAddressClick={handleSuggestedAddressClick} />;
  }
  return <AddressFormFormik aptFieldOpen={aptFieldOpen} countryTarget={countryTarget} statesOrProvinces={statesOrProvinces} dropdownIsOpen={dropdownIsOpen} suggestedAddresses={suggestedAddresses} loading={loading} setLoading={setLoading} checkAddressSuggestions={checkAddressSuggestions} setDropdownIsOpen={setDropdownIsOpen} setSuggestedAddresses={setSuggestedAddresses} setAptFieldOpen={setAptFieldOpen} handleSuggestedAddressClick={handleSuggestedAddressClick} />;
}
type RenderAddressFormProps = {
  aptFieldOpen: boolean;
  countryTarget?: CountryTarget;
  statesOrProvinces: {
    abbr: string;
    name: string;
  }[];
  dropdownIsOpen: boolean;
  suggestedAddresses: SuggestedAddressInterface[] | null;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  checkAddressSuggestions: (suggestAddressInput: string) => Promise<SuggestionResponseInterface | null>;
  setDropdownIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSuggestedAddresses: React.Dispatch<React.SetStateAction<SuggestedAddressInterface[] | null>>;
  setAptFieldOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSuggestedAddressClick: (suggestedAddress: SuggestedAddressInterface) => Promise<FormatAddressResponseInterface | null>;
};
function AddressFormFormik(t0) {
  const $ = _c(69);
  const {
    aptFieldOpen,
    countryTarget,
    statesOrProvinces,
    dropdownIsOpen,
    suggestedAddresses,
    loading,
    setLoading,
    checkAddressSuggestions,
    setDropdownIsOpen,
    setSuggestedAddresses,
    setAptFieldOpen,
    handleSuggestedAddressClick
  } = t0;
  const {
    values,
    handleChange,
    handleBlur,
    touched: t1,
    errors: t2,
    setValues,
    setFieldValue
  } = useFormikContext();
  const {
    address: touchedAddress
  } = t1;
  const {
    address: addressErrors
  } = t2;
  const addressValues = values.address;
  let t3;
  let t4;
  if ($[0] !== addressValues.apt || $[1] !== setAptFieldOpen) {
    t3 = () => {
      if (addressValues.apt) {
        setAptFieldOpen(true);
      }
    };
    t4 = [addressValues.apt, setAptFieldOpen];
    $[0] = addressValues.apt;
    $[1] = setAptFieldOpen;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== setAptFieldOpen || $[5] !== setValues || $[6] !== values) {
    t5 = formattedAddress => {
      setAptFieldOpen(!!formattedAddress.apt);
      applyFormattedAddressToFormik(formattedAddress, values, setValues);
    };
    $[4] = setAptFieldOpen;
    $[5] = setValues;
    $[6] = values;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== handleSuggestedAddressClick || $[9] !== t5) {
    t6 = createSuggestedAddressSelectionHandler(handleSuggestedAddressClick, t5);
    $[8] = handleSuggestedAddressClick;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== checkAddressSuggestions || $[12] !== dropdownIsOpen || $[13] !== loading || $[14] !== setDropdownIsOpen || $[15] !== setLoading || $[16] !== setSuggestedAddresses || $[17] !== suggestedAddresses || $[18] !== t6) {
    t7 = <Grid.Item><StreetAddressLine formLibrary="formik" dropdownIsOpen={dropdownIsOpen} suggestedAddresses={suggestedAddresses} handleSuggestedAddressClick={t6} loading={loading} setLoading={setLoading} checkAddressSuggestions={checkAddressSuggestions} setDropdownIsOpen={setDropdownIsOpen} setSuggestedAddresses={setSuggestedAddresses} /></Grid.Item>;
    $[11] = checkAddressSuggestions;
    $[12] = dropdownIsOpen;
    $[13] = loading;
    $[14] = setDropdownIsOpen;
    $[15] = setLoading;
    $[16] = setSuggestedAddresses;
    $[17] = suggestedAddresses;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== addressValues.apt || $[21] !== aptFieldOpen || $[22] !== handleBlur || $[23] !== handleChange || $[24] !== setAptFieldOpen) {
    t8 = <Grid.Item>{aptFieldOpen ? <TextField data-testid="apt" data-cs-mask={true} name="address.apt" type="text" label="Apt/suite (optional)" value={addressValues.apt} onBlur={handleBlur} onChange={handleChange} /> : <Button data-testid="apt-btn" aria-expanded="true" className="sparky-u-width-fit-content sparky-u-typography-preset-7" variant="link" iconName="plus" iconSize="md" text="Add apt/suite/other" type="button" onClick={() => setAptFieldOpen(true)} />}</Grid.Item>;
    $[20] = addressValues.apt;
    $[21] = aptFieldOpen;
    $[22] = handleBlur;
    $[23] = handleChange;
    $[24] = setAptFieldOpen;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const t9 = touchedAddress?.zipCode && !!addressErrors?.zipCode;
  let t10;
  if ($[26] !== setFieldValue) {
    t10 = ev => {
      ev.target.value = removeSpecialCharacters(ev.target.value);
      zipCodeSetter(ev, "address.zipCode", (_, value) => {
        setFieldValue("address.zipCode", value);
      });
    };
    $[26] = setFieldValue;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== addressErrors?.zipCode || $[29] !== touchedAddress?.zipCode) {
    t11 = touchedAddress?.zipCode ? getFieldErrorMessage(addressErrors?.zipCode) : undefined;
    $[28] = addressErrors?.zipCode;
    $[29] = touchedAddress?.zipCode;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const t12 = countryTarget !== CountryTarget.BOTH && !isLocaleCA() ? 5 : undefined;
  let t13;
  if ($[31] !== addressValues.zipCode || $[32] !== handleBlur || $[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t9) {
    t13 = <Grid.Item><TextField data-testid="zipCode" data-cs-mask={true} wrapperTestId="zipCode-field" name="address.zipCode" type="text" label={isLocaleCA() ? "Postal code" : "Zip code"} value={addressValues.zipCode} isError={t9} onBlur={handleBlur} onChange={t10} fieldNote={t11} maxLength={t12} /></Grid.Item>;
    $[31] = addressValues.zipCode;
    $[32] = handleBlur;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t9;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const t14 = addressValues.state ? "sparky-is-filled" : undefined;
  let t15;
  if ($[38] !== statesOrProvinces) {
    t15 = statesOrProvinces?.map(_temp);
    $[38] = statesOrProvinces;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const t16 = touchedAddress?.state && !!addressErrors?.state;
  let t17;
  if ($[40] !== addressErrors?.state || $[41] !== touchedAddress?.state) {
    t17 = touchedAddress?.state ? getFieldErrorMessage(addressErrors?.state) : undefined;
    $[40] = addressErrors?.state;
    $[41] = touchedAddress?.state;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const t18 = touchedAddress?.state && !!addressErrors?.state ? "exclamation-circle-fill" : undefined;
  let t19;
  if ($[43] !== addressValues.state || $[44] !== handleBlur || $[45] !== handleChange || $[46] !== t14 || $[47] !== t15 || $[48] !== t16 || $[49] !== t17 || $[50] !== t18) {
    t19 = <Grid.Item data-testid="state-field"><SelectField className={t14} data-testid="state" data-cs-mask={true} name="address.state" items={t15} label={isLocaleCA() ? "Province" : "State"} value={addressValues.state} isError={t16} onBlur={handleBlur} onChange={handleChange} fieldNote={t17} iconName={t18} /></Grid.Item>;
    $[43] = addressValues.state;
    $[44] = handleBlur;
    $[45] = handleChange;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t13 || $[53] !== t19) {
    t20 = <Grid.Item><Grid variant="side-by-side" gap="sm">{t13}{t19}</Grid></Grid.Item>;
    $[52] = t13;
    $[53] = t19;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  const t21 = touchedAddress?.city && !!addressErrors?.city;
  let t22;
  if ($[55] !== addressErrors?.city || $[56] !== touchedAddress?.city) {
    t22 = touchedAddress?.city ? getFieldErrorMessage(addressErrors?.city) : undefined;
    $[55] = addressErrors?.city;
    $[56] = touchedAddress?.city;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== addressValues.city || $[59] !== handleBlur || $[60] !== handleChange || $[61] !== t21 || $[62] !== t22) {
    t23 = <Grid.Item><TextField data-testid="city" data-cs-mask={true} wrapperTestId="city-field" name="address.city" type="text" label="City" value={addressValues.city} isError={t21} onBlur={handleBlur} onChange={handleChange} fieldNote={t22} /></Grid.Item>;
    $[58] = addressValues.city;
    $[59] = handleBlur;
    $[60] = handleChange;
    $[61] = t21;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== t20 || $[65] !== t23 || $[66] !== t7 || $[67] !== t8) {
    t24 = <Grid gap="sm">{t7}{t8}{t20}{t23}</Grid>;
    $[64] = t20;
    $[65] = t23;
    $[66] = t7;
    $[67] = t8;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  return t24;
}
function _temp(t0) {
  const {
    abbr,
    name
  } = t0;
  return {
    label: name,
    value: abbr,
    disabled: false
  };
}
function AddressFormRHF({
  aptFieldOpen,
  countryTarget,
  statesOrProvinces,
  dropdownIsOpen,
  suggestedAddresses,
  loading,
  setLoading,
  checkAddressSuggestions,
  setDropdownIsOpen,
  setSuggestedAddresses,
  setAptFieldOpen,
  handleSuggestedAddressClick
}: Readonly<RenderAddressFormProps>) {
  const {
    register,
    watch,
    setValue,
    formState: {
      errors,
      touchedFields,
      isSubmitted
    }
  } = useFormContext<AddressFormValues>();
  const addressValues = watch('address');
  const touchedAddress = touchedFields.address;
  const addressErrors = errors.address;
  const shouldShowFieldError = (fieldTouched: boolean | undefined, fieldError: unknown) => !!fieldError && (isSubmitted || !!fieldTouched);
  const aptField = register('address.apt');
  const zipCodeField = register('address.zipCode');
  const stateField = register('address.state');
  const cityField = register('address.city');
  const stateErrorMessage = getFieldErrorMessage(addressErrors?.state);
  const hasUndefinedStateTypeError = stateErrorMessage === 'Invalid input: expected string, received undefined';
  let displayStateErrorMessage = stateErrorMessage;
  if (hasUndefinedStateTypeError) {
    displayStateErrorMessage = isLocaleCA() ? provinceRequiredMsg : stateRequiredMsg;
  }
  useEffect(() => {
    if (addressValues?.apt) {
      setAptFieldOpen(true);
    }
  }, [addressValues?.apt, setAptFieldOpen]);
  return <Grid gap="sm">
            <Grid.Item>
                <StreetAddressLine formLibrary="rhf" dropdownIsOpen={dropdownIsOpen} suggestedAddresses={suggestedAddresses} handleSuggestedAddressClick={createSuggestedAddressSelectionHandler(handleSuggestedAddressClick, formattedAddress => {
        setAptFieldOpen(!!formattedAddress.apt);
        applyFormattedAddressToRHF(formattedAddress, setValue);
      })} loading={loading} setLoading={setLoading} checkAddressSuggestions={checkAddressSuggestions} setDropdownIsOpen={setDropdownIsOpen} setSuggestedAddresses={setSuggestedAddresses} />
            </Grid.Item>
            <Grid.Item>
                {aptFieldOpen ? <TextField data-testid="apt" data-cs-mask name={aptField.name} ref={aptField.ref} type="text" label="Apt/suite (optional)" value={addressValues?.apt ?? ''} onBlur={aptField.onBlur} onChange={aptField.onChange} /> : <Button data-testid="apt-btn" aria-expanded="true" className="sparky-u-width-fit-content sparky-u-typography-preset-7" variant="link" iconName="plus" iconSize="md" text="Add apt/suite/other" type="button" onClick={() => setAptFieldOpen(true)} />}
            </Grid.Item>

            <Grid.Item>
                <Grid variant="side-by-side" gap="sm">
                    <Grid.Item>
                        <TextField data-testid="zipCode" data-cs-mask wrapperTestId="zipCode-field" name={zipCodeField.name} ref={zipCodeField.ref} type="text" label={isLocaleCA() ? 'Postal code' : 'Zip code'} value={addressValues?.zipCode ?? ''} isError={shouldShowFieldError(!!touchedAddress?.zipCode, addressErrors?.zipCode)} onBlur={zipCodeField.onBlur} onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
            ev.target.value = removeSpecialCharacters(ev.target.value);
            zipCodeSetter(ev, 'address.zipCode', (_, value) => {
              setValue('address.zipCode', value, {
                shouldDirty: true,
                shouldValidate: true
              });
            });
          }} fieldNote={shouldShowFieldError(!!touchedAddress?.zipCode, addressErrors?.zipCode) ? getFieldErrorMessage(addressErrors?.zipCode) : undefined} maxLength={countryTarget !== CountryTarget.BOTH && !isLocaleCA() ? 5 : undefined} />
                    </Grid.Item>
                    <Grid.Item data-testid="state-field">
                        <SelectField className={addressValues?.state ? 'sparky-is-filled' : undefined} data-testid="state" data-cs-mask name={stateField.name} items={statesOrProvinces?.map(({
            abbr,
            name
          }) => ({
            label: name,
            value: abbr,
            disabled: false
          }))} label={isLocaleCA() ? 'Province' : 'State'} selectedValue={addressValues?.state ?? ''} value={addressValues?.state ?? ''} isError={shouldShowFieldError(!!touchedAddress?.state, addressErrors?.state)} onBlur={stateField.onBlur} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
            setValue('address.state', event.target.value, {
              shouldDirty: true,
              shouldValidate: true,
              shouldTouch: true
            });
          }} fieldNote={shouldShowFieldError(!!touchedAddress?.state, addressErrors?.state) ? displayStateErrorMessage : undefined} iconName={shouldShowFieldError(!!touchedAddress?.state, addressErrors?.state) ? 'exclamation-circle-fill' : undefined} />
                    </Grid.Item>
                </Grid>
            </Grid.Item>

            <Grid.Item>
                <TextField data-testid="city" data-cs-mask wrapperTestId="city-field" name={cityField.name} ref={cityField.ref} type="text" label="City" value={addressValues?.city ?? ''} isError={shouldShowFieldError(!!touchedAddress?.city, addressErrors?.city)} onBlur={cityField.onBlur} onChange={cityField.onChange} fieldNote={shouldShowFieldError(!!touchedAddress?.city, addressErrors?.city) ? getFieldErrorMessage(addressErrors?.city) : undefined} />
            </Grid.Item>
        </Grid>;
}